import { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmissionColumn } from '../../api/forms';
import type { SubmissionView } from '../../api/forms';
import { COARSE, POPOVER_SHEET, useMediaQuery } from '../../shared/media';

/**
 * A popover anchored to the button that opened it.
 *
 * Closes on Escape and on a click anywhere outside, because these hold a
 * working set of choices and there is no Cancel — leaving is the cancel.
 *
 * Under 700px it is a bottom sheet instead (F5): the anchor and the width are
 * ignored, a scrim sits behind it, and tapping the scrim is the click outside.
 */
export function Popover({ open, onClose, width, align = 'right', children }: {
  open: boolean;
  onClose: () => void;
  width: number;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const sheet = useMediaQuery(POPOVER_SHEET);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      // The anchor itself is outside the popover but must not close it — its
      // own click handler is what toggles the thing.
      const el = ref.current;
      if (el && !el.parentElement?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      {sheet && <div className="sl-pop-scrim" onClick={onClose} />}
      <div ref={ref} className={`sl-pop ${align === 'left' ? 'left' : ''}${sheet ? ' sheet' : ''}`} style={sheet ? undefined : { width }}>
        {children}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

/**
 * Which columns the table shows, and in what order.
 *
 * Ordering is drag-within-this-list; there is deliberately no drag-the-header
 * interaction in the table, where a mis-drop during triage would be costly.
 */
export function ColumnsPanel({ columns, selected, onChange, onReset, onDone }: {
  columns: SubmissionColumn[];
  selected: string[];
  onChange: (next: string[]) => void;
  onReset: () => void;
  onDone: () => void;
}) {
  const [find, setFind] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  /*
    F1' — a tap path beside the drag. HTML5 drag and drop does not fire on a
    touch screen, and the panel is a sheet under 700px, so the arrows appear
    wherever dragging is not the way in. The drag handlers and the grip are
    untouched: a mouse still reorders exactly as it did.
  */
  const sheet = useMediaQuery(POPOVER_SHEET);
  const coarse = useMediaQuery(COARSE);
  const arrows = sheet || coarse;

  const term = find.trim().toLowerCase();
  const visible = useMemo(
    () => (term
      ? columns.filter((c) => c.label.toLowerCase().includes(term) || (c.key || '').toLowerCase().includes(term))
      : columns),
    [columns, term]
  );

  // Chosen columns first, in their chosen order, so reordering is a plain
  // top-of-list drag rather than a hunt through every available field.
  const ordered = useMemo(() => {
    const chosen = selected
      .map((id) => visible.find((c) => c.id === id))
      .filter(Boolean) as SubmissionColumn[];
    const rest = visible.filter((c) => !selected.includes(c.id));
    return { chosen, rest };
  }, [visible, selected]);

  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...selected, id] : selected.filter((c) => c !== id));

  /** One step up or down the chosen list — the arrows' whole job. */
  const nudge = (id: string, delta: number) => {
    const at = selected.indexOf(id);
    const to = at + delta;
    if (at < 0 || to < 0 || to >= selected.length) return;
    const next = [...selected];
    next.splice(to, 0, next.splice(at, 1)[0]);
    onChange(next);
  };

  const move = (from: string, to: string) => {
    if (from === to) return;
    const next = selected.filter((c) => c !== from);
    const at = next.indexOf(to);
    next.splice(at < 0 ? next.length : at, 0, from);
    onChange(next);
  };

  const groups = useMemo(() => {
    const out = new Map<string, SubmissionColumn[]>();
    for (const c of ordered.rest) {
      if (!out.has(c.group)) out.set(c.group, []);
      out.get(c.group)!.push(c);
    }
    return out;
  }, [ordered.rest]);

  return (
    <>
      <div className="sl-pop-head">
        Columns
        <span className="count">{selected.length} of {columns.length} shown</span>
      </div>
      <div className="sl-pop-body">
        <input className="sl-find" placeholder="Find a field…" value={find} onChange={(e) => setFind(e.target.value)} autoFocus />

        {ordered.chosen.length > 0 && (
          <>
            <div className="sl-group">Shown · drag to reorder</div>
            {ordered.chosen.map((c) => (
              <div
                key={c.id}
                className={`sl-col ${dragging === c.id ? 'dragging' : ''} ${over === c.id ? 'over' : ''}`}
                draggable={!c.locked}
                onDragStart={() => setDragging(c.id)}
                onDragEnd={() => { setDragging(null); setOver(null); }}
                onDragOver={(e) => { e.preventDefault(); setOver(c.id); }}
                onDrop={(e) => { e.preventDefault(); if (dragging) move(dragging, c.id); setDragging(null); setOver(null); }}
              >
                <span className={`grip ${c.locked ? 'off' : ''}`}>⠿</span>
                {arrows && (
                  <span className="sl-col-move">
                    <button
                      type="button" title={`Move ${c.label} up`} aria-label={`Move ${c.label} up`}
                      disabled={c.locked || selected.indexOf(c.id) <= 0}
                      onClick={() => nudge(c.id, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button" title={`Move ${c.label} down`} aria-label={`Move ${c.label} down`}
                      disabled={c.locked || selected.indexOf(c.id) >= selected.length - 1}
                      onClick={() => nudge(c.id, 1)}
                    >
                      ↓
                    </button>
                  </span>
                )}
                <input type="checkbox" checked disabled={c.locked} onChange={() => toggle(c.id, false)} />
                <span className="name">{c.label}</span>
                <span className={`key ${c.locked ? 'note' : ''} ${c.orphan ? 'warn' : ''}`}>
                  {c.locked ? 'always shown' : c.orphan ? 'no longer on the form' : c.key || ''}
                </span>
              </div>
            ))}
          </>
        )}

        {[...groups.entries()].map(([group, cols]) => (
          <div key={group}>
            <div className="sl-group">{group}</div>
            {cols.map((c) => (
              <div className="sl-col" key={c.id}>
                <span className="grip off">⠿</span>
                <input type="checkbox" checked={false} onChange={() => toggle(c.id, true)} />
                <span className="name">{c.label}</span>
                <span className={`key ${c.orphan ? 'warn' : ''}`}>{c.orphan ? 'no longer on the form' : c.key || ''}</span>
              </div>
            ))}
          </div>
        ))}

        {visible.length === 0 && (
          <div className="sl-pop-empty">No field matches “{find}”.</div>
        )}

        <div className="fc-note" style={{ marginTop: 14 }}>
          A repeating group cannot be one cell. It offers a count, a sum for each numeric field, and the first instance's
          value — anything more belongs in the drawer.
        </div>
      </div>
      <div className="sl-pop-foot">
        <button type="button" className="fs-btn text sm muted" onClick={onReset}>Reset to default</button>
        <span style={{ flex: 1 }} />
        <button className="fs-btn primary sm" onClick={onDone}>Done</button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

/**
 * Saved views.
 *
 * A view stores filters, column set, column order and sort together. Personal
 * column choices persist per user per form without one — a view is only needed
 * when the set should have a name or be shared.
 */
export function ViewsPanel({ views, activeId, canEdit, onApply, onSave, onUpdate, onDelete }: {
  views: SubmissionView[];
  activeId: number | null;
  canEdit: (v: SubmissionView) => boolean;
  onApply: (v: SubmissionView) => void;
  onSave: (name: string, shared: boolean) => Promise<void>;
  /**
   * Editing a view (R8): a new name, a change of sharing, or the table's
   * current filters, columns and sort written over what it held.
   */
  onUpdate?: (v: SubmissionView, change: { name?: string; shared?: boolean; useCurrent?: boolean }) => Promise<void>;
  onDelete: (v: SubmissionView) => void;
}) {
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  /** the view whose name is being typed, and the draft */
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);

  const commitRename = async () => {
    const v = views.find((x) => x.id === renaming?.id);
    const next = renaming?.name.trim();
    if (!v || !next || !onUpdate) { setRenaming(null); return; }
    if (next !== v.name) await onUpdate(v, { name: next }).catch(() => undefined);
    setRenaming(null);
  };

  const section = (label: string, list: SubmissionView[]) => (
    list.length > 0 && (
      <>
        <div className="sl-group">{label}</div>
        {list.map((v) => (
          <div className={`sl-view-row${activeId === v.id ? ' on' : ''}`} key={v.id}>
            {renaming?.id === v.id ? (
              <input
                className="sl-find" aria-label={`Rename “${v.name}”`} autoFocus
                value={renaming.name}
                onChange={(e) => setRenaming({ id: v.id, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void commitRename(); }
                  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setRenaming(null); }
                }}
                onBlur={() => void commitRename()}
              />
            ) : (
              <span className="name" onClick={() => onApply(v)}>{v.name}</span>
            )}
            {canEdit(v) && onUpdate && renaming?.id !== v.id && (
              <span className="sl-view-tools">
                {activeId === v.id && (
                  <button
                    type="button" className="fs-btn text sm" title="Write the table's current filters, columns and sort into this view"
                    onClick={() => void onUpdate(v, { useCurrent: true }).catch(() => undefined)}
                  >
                    Use current filters
                  </button>
                )}
                <button type="button" className="fs-btn text sm" onClick={() => setRenaming({ id: v.id, name: v.name })}>Rename</button>
                <button
                  type="button" className="fs-btn text sm"
                  title={v.shared ? 'Make it yours alone' : 'Share it with everyone on this account'}
                  onClick={() => void onUpdate(v, { shared: !v.shared }).catch(() => undefined)}
                >
                  {v.shared ? 'Unshare' : 'Share'}
                </button>
              </span>
            )}
            {canEdit(v) && renaming?.id !== v.id && (
              <button className="fc-x" title={`Delete “${v.name}”`} onClick={() => onDelete(v)}>×</button>
            )}
          </div>
        ))}
      </>
    )
  );

  return (
    <>
      <div className="sl-pop-head">Views</div>
      <div className="sl-pop-body">
        {section('Shared with the team', views.filter((v) => v.shared))}
        {section('Only me', views.filter((v) => !v.shared))}
        {views.length === 0 && (
          <div className="sl-pop-empty">No saved views yet.</div>
        )}

        <div className="sl-save-block">
          <b>Save current filters + columns as a view</b>
          <input
            className="sl-find"
            placeholder="Name this view…"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label style={{ display: 'flex', gap: 7, alignItems: 'center', margin: '10px 0', fontSize: 13 }}>
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
            Share with everyone on this account
          </label>
          <button
            className="fs-btn primary sm"
            disabled={!name.trim() || saving}
            onClick={async () => {
              setSaving(true);
              try { await onSave(name.trim(), shared); setName(''); setShared(false); }
              finally { setSaving(false); }
            }}
          >
            {saving ? 'Saving…' : 'Save view'}
          </button>
        </div>

        <div className="fc-note" style={{ marginTop: 12 }}>
          A view stores <b>filters, column set, column order and sort</b> together. Personal column choices persist per user
          per form without one — a view is only needed when it should have a name or be shared.
        </div>
      </div>
    </>
  );
}
